// Ctrl/Cmd+K global navigation palette.
// Same data source as the sidebar search — respects role-filtered sections.
// Triggered by:
//   - Ctrl+K / Cmd+K keyboard shortcut (registered in Sidebar mount)
//   - The "Search…" pill at the top of the sidebar
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { MagnifyingGlass, X, ArrowRight } from "@phosphor-icons/react";
import { flattenForPalette } from "../utils/navSearch";
import { useGuardedNavigate } from "./NavigationGuard";

const GlobalNavSearch = ({ open, onClose, sections }) => {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useGuardedNavigate();

  const results = useMemo(() => flattenForPalette(sections, query).slice(0, 30), [sections, query]);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Reset activeIdx when result list changes
  useEffect(() => { setActiveIdx(0); }, [query]);

  const choose = useCallback((row) => {
    if (!row) return;
    navigate(row.item.path);
    onClose();
  }, [navigate, onClose]);

  // Keyboard navigation inside palette
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(results.length - 1, i + 1)); }
      else if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
      else if (e.key === "Enter")     { e.preventDefault(); choose(results[activeIdx]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, results, activeIdx, choose, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      data-testid="global-nav-search-overlay"
      className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-start justify-center pt-20 px-4"
      onClick={onClose}
    >
      <div
        data-testid="global-nav-search-panel"
        className="w-full max-w-2xl bg-white shadow-2xl rounded-md overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 p-4 border-b border-slate-200">
          <MagnifyingGlass size={20} className="text-slate-400 shrink-0" />
          <input
            data-testid="global-nav-search-input"
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, modules, settings… (try 'brand', 'quote', 'lead')"
            className="flex-1 outline-none text-base bg-transparent placeholder:text-slate-400"
          />
          <button
            data-testid="global-nav-search-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 shrink-0"
            aria-label="Close search"
          >
            <X size={18} />
          </button>
        </div>
        {/* Results list */}
        <div className="max-h-[60vh] overflow-y-auto py-1" data-testid="global-nav-search-results">
          {results.length === 0 ? (
            <div data-testid="global-nav-search-empty" className="p-8 text-center text-slate-400 text-sm">
              No menu item matches “{query.trim() || "your search"}”.
            </div>
          ) : (
            results.map((row, idx) => {
              const Icon = row.item.icon;
              const active = idx === activeIdx;
              return (
                <button
                  key={`${row.sectionId}-${row.item.key}`}
                  data-testid={`global-nav-result-${idx}`}
                  onClick={() => choose(row)}
                  onMouseEnter={() => setActiveIdx(idx)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    active ? "bg-[#002FA7] text-white" : "hover:bg-slate-50 text-slate-800"
                  }`}
                >
                  {Icon && <Icon size={16} weight={active ? "fill" : "regular"} className="shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{row.item.label}</div>
                    <div className={`text-[10px] uppercase tracking-wider ${active ? "text-blue-100" : "text-slate-400"}`}>
                      {row.sectionLabel} · {row.item.path}
                    </div>
                  </div>
                  <ArrowRight size={14} weight="bold" className={`shrink-0 ${active ? "opacity-100" : "opacity-30"}`} />
                </button>
              );
            })
          )}
        </div>
        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 flex items-center justify-between">
          <span><kbd className="bg-white border border-slate-300 px-1.5 py-0.5 rounded text-[10px] font-mono">↑↓</kbd> Navigate · <kbd className="bg-white border border-slate-300 px-1.5 py-0.5 rounded text-[10px] font-mono">↵</kbd> Open · <kbd className="bg-white border border-slate-300 px-1.5 py-0.5 rounded text-[10px] font-mono">Esc</kbd> Close</span>
          <span>{results.length} result{results.length === 1 ? "" : "s"}</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalNavSearch;
